// src/components/oci/DataPriceAnalysisWidget.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  alpha,
  useTheme,
  Grid,
  Paper,
  Chip,
  Tooltip as MuiTooltip,
  IconButton
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
  Scatter
} from 'recharts';
import { InfoOutlined, RefreshOutlined, TrendingUp } from '@mui/icons-material';
import api from '../../services/api';

// Region mapping between OCI region names and rsyslog region codes
const REGION_MAPPING = {
  'ap-mumbai-1': 'BOM',
  'ap-hyderabad-1': 'HYD',
  'me-abudhabi-1': 'AUH',
  'ap-singapore-1': 'SIN',
  'af-johannesburg-1': 'JNB'
};

const DataPriceAnalysisWidget = ({
  initialRegion = '',
  initialCompartment = '',
  startDate,
  endDate,
  onRefresh
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [region, setRegion] = useState(initialRegion);
  const [compartment, setCompartment] = useState(initialCompartment);
  const [regions, setRegions] = useState([]);
  const [compartments, setCompartments] = useState([]);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [correlationData, setCorrelationData] = useState({
    coefficient: 0,
    strength: '',
    description: ''
  });
  const [metrics, setMetrics] = useState({
    totalDataSize: 0,
    totalCost: 0,
    averageCostPerGB: 0,
    predictedCostIncrease: 0
  });

  // Format currency helper
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '₹0.00';
    return `₹${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Format bytes helper
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  // Fetch regions
  const fetchRegions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/oci/regions');
      setRegions(response.data || []);
    } catch (err) {
      console.error("Error fetching regions:", err);
      setError("Failed to load regions");
    }
  };

  // Fetch compartments based on selected region
  const fetchCompartments = async (selectedRegion) => {
    try {
      const url = `/api/oci/compartments${selectedRegion ? `?region=${selectedRegion}` : ''}`;
      const response = await api.get(url);
      setCompartments(response.data || []);
    } catch (err) {
      console.error("Error fetching compartments:", err);
      setError("Failed to load compartments");
    }
  };

  // Fetch time series data for analysis
  const fetchTimeSeriesData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Convert OCI region to RSYSLOG region code
      const rsyslogRegion = region ? REGION_MAPPING[region] : '';

      // Fetch RSYSLOG data size and cost data in one call
      const rsyslogUrl = `/api/clients/logs/timeseries?${rsyslogRegion ? `region=${rsyslogRegion}&` : ''}start_date=${startDate}&end_date=${endDate}`;
      console.log('Fetching data with URL:', rsyslogUrl); // Debug log

      const response = await api.get(rsyslogUrl);
      console.log('API Response:', response.data); // Debug log

      // The API returns {data: [...], metadata: {...}}
      const timeseriesData = response.data?.data || [];
      console.log('Timeseries entries:', timeseriesData.length); // Debug log

      // Process the data - ensure all required fields are present and properly formatted
      const processedData = timeseriesData.map(entry => {
        // Use total_rsyslog_gb for RSYSLOG data size
        const dataSize = Number(entry.total_rsyslog_gb || 0);
        const cost = Number(entry.total_cost || 0);
        const costPerGB = dataSize > 0 ? cost / dataSize : 0;

        return {
          date: entry.date,
          dataSize: parseFloat(dataSize.toFixed(4)),
          cost: parseFloat(cost.toFixed(4)),

          costPerGB: parseFloat(costPerGB.toFixed(4))
        };
      });

      // Sort by date
      processedData.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Calculate correlation
      const correlation = calculateCorrelation(processedData);

      // Calculate metrics
      const metrics = {
        totalDataSize: processedData.reduce((sum, entry) => sum + entry.dataSize, 0),
        totalCost: processedData.reduce((sum, entry) => sum + entry.cost, 0),
        averageCostPerGB: (() => {
          const totalCost = processedData.reduce((sum, entry) => sum + entry.cost, 0);
          const totalDataSize = processedData.reduce((sum, entry) => sum + entry.dataSize, 0);
          return totalDataSize > 0 ? totalCost / totalDataSize : 0;
        })()
      };

      console.log('Processed Time Series Data:', processedData); // Debug log

      setTimeSeriesData(processedData);
      setCorrelationData(correlation);
      setMetrics(metrics);

    } catch (err) {
      console.error("Error fetching analysis data:", err);
      setError("Failed to load analysis data");
    } finally {
      setLoading(false);
    }
  };

  // Calculate correlation between data size and cost
  const calculateCorrelation = (data) => {
    const n = data.length;
    if (n < 2) return { coefficient: 0, strength: 'Unknown', description: 'Insufficient data' };

    const sumX = data.reduce((acc, d) => acc + d.dataSize, 0);
    const sumY = data.reduce((acc, d) => acc + d.cost, 0);
    const sumXY = data.reduce((acc, d) => acc + (d.dataSize * d.cost), 0);
    const sumX2 = data.reduce((acc, d) => acc + (d.dataSize * d.dataSize), 0);
    const sumY2 = data.reduce((acc, d) => acc + (d.cost * d.cost), 0);

    const r = (n * sumXY - sumX * sumY) /
      Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

    let strength = '';
    let description = '';

    if (r >= 0.7) {
      strength = 'Strong Positive';
      description = 'Costs increase significantly with data size';
    } else if (r >= 0.3) {
      strength = 'Moderate Positive';
      description = 'Costs tend to increase with data size';
    } else if (r >= -0.3) {
      strength = 'Weak';
      description = 'No clear relationship between costs and data size';
    } else if (r >= -0.7) {
      strength = 'Moderate Negative';
      description = 'Costs tend to decrease with data size';
    } else {
      strength = 'Strong Negative';
      description = 'Costs decrease significantly with data size';
    }

    return { coefficient: r, strength, description };
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    // Find the data point for this date
    const dataPoint = timeSeriesData.find(item => item.date === label);
    if (!dataPoint) return null;

    return (
      <Paper
        elevation={3}
        sx={{
          p: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          borderRadius: '8px',
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          {new Date(label).toLocaleDateString()}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Total Cost: {formatCurrency(dataPoint.cost)}
          </Typography>
          <Typography variant="body2" color="secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Data Size: {formatBytes(dataPoint.dataSize * 1024 * 1024 * 1024)}
          </Typography>
          <Divider sx={{ my: 1 }} />

          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            Cost per GB: {formatCurrency(dataPoint.costPerGB)}
          </Typography>
        </Box>
      </Paper>
    );
  };

  // Calculate Y-axis domains based on data
  const getAxisDomains = () => {
    if (!timeSeriesData.length) return { cost: [0, 100], size: [0, 10] };

    const maxCost = Math.max(...timeSeriesData.map(d => d.cost));
    const maxSize = Math.max(...timeSeriesData.map(d => d.dataSize));
    const minCost = Math.min(...timeSeriesData.map(d => d.cost));
    const minSize = Math.min(...timeSeriesData.map(d => d.dataSize));

    // Add 10% padding to the domains
    const costPadding = (maxCost - minCost) * 0.1;
    const sizePadding = (maxSize - minSize) * 0.1;

    return {
      cost: [
        Math.max(0, minCost - costPadding),
        maxCost + costPadding
      ],
      size: [
        Math.max(0, minSize - sizePadding),
        maxSize + sizePadding
      ]
    };
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  useEffect(() => {
    if (region) {
      fetchCompartments(region);
    }
  }, [region]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchTimeSeriesData();
    }
  }, [region, compartment, startDate, endDate]);

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: '16px',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" fontWeight="medium">
            Data Size vs. Cost Analysis
          </Typography>
          <Box display="flex" gap={1}>
            <MuiTooltip title="Refresh data">
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    fetchTimeSeriesData();
                    if (onRefresh) onRefresh();
                  }}
                  disabled={loading}
                >
                  <RefreshOutlined />
                </IconButton>
              </span>
            </MuiTooltip>
            <MuiTooltip title="Analysis helps understand the relationship between data size and costs">
              <IconButton size="small">
                <InfoOutlined />
              </IconButton>
            </MuiTooltip>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    height: '100%',
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    borderRadius: '12px',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Data Size
                  </Typography>
                  <Typography variant="h6" fontWeight="medium">
                    {formatBytes(metrics.totalDataSize * 1024 * 1024 * 1024)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    height: '100%',
                    backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                    borderRadius: '12px',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Cost
                  </Typography>
                  <Typography variant="h6" fontWeight="medium">
                    {formatCurrency(metrics.totalCost)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    height: '100%',
                    backgroundColor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                    borderRadius: '12px',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Average Cost per GB
                  </Typography>
                  <Typography variant="h6" fontWeight="medium">
                    {formatCurrency(metrics.averageCostPerGB)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  borderRadius: '12px',
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <TrendingUp fontSize="small" color={correlationData.coefficient >= 0 ? "success" : "error"} />
                  <Typography variant="body2">
                    {correlationData.description}
                  </Typography>
                  <Chip
                    label={correlationData.strength}
                    size="small"
                    color={
                      correlationData.strength.includes('Strong') ? 'primary' :
                        correlationData.strength.includes('Moderate') ? 'secondary' :
                          'default'
                    }
                    sx={{ ml: 'auto' }}
                  />
                </Box>
              </Paper>
            </Box>

            <Box sx={{ width: '100%', height: 400 }}>
              <ResponsiveContainer>
                <ComposedChart
                  data={timeSeriesData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
                  <XAxis
                    dataKey="date"
                    stroke={theme.palette.text.secondary}
                    tick={{ fill: theme.palette.text.secondary }}
                    tickFormatter={(date) => new Date(date).toLocaleDateString()}
                  />
                  <YAxis
                    yAxisId="cost"
                    orientation="left"
                    stroke={theme.palette.primary.main}
                    tick={{ fill: theme.palette.text.secondary }}
                    tickFormatter={(value) => `₹${value.toFixed(0)}`}
                    domain={getAxisDomains().cost}
                  />
                  <YAxis
                    yAxisId="size"
                    orientation="right"
                    stroke={theme.palette.secondary.main}
                    tick={{ fill: theme.palette.text.secondary }}
                    tickFormatter={(value) => `${value.toFixed(1)} GB`}
                    domain={getAxisDomains().size}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />

                  {/* Data Size Line */}
                  <Line
                    yAxisId="size"
                    type="monotone"
                    dataKey="dataSize"
                    name="Data Size (GB)"
                    stroke={theme.palette.secondary.main}
                    strokeWidth={2}
                    dot={{ fill: theme.palette.secondary.main, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                    connectNulls
                  />

                  {/* Total Cost Line */}
                  <Line
                    yAxisId="cost"
                    type="monotone"
                    dataKey="cost"
                    name="Total Cost"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={{ fill: theme.palette.primary.main, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                    connectNulls
                  />

                  {/* Storage Cost Area */}
                  <Area
                    yAxisId="cost"
                    type="monotone"
                    dataKey="storageCost"
                    name="Storage Cost"
                    fill={alpha(theme.palette.info.main, 0.1)}
                    stroke={theme.palette.info.main}
                    strokeWidth={1}
                    stackId="costs"
                    isAnimationActive={false}
                    connectNulls
                  />

                  {/* Compute Cost Area */}
                  <Area
                    yAxisId="cost"
                    type="monotone"
                    dataKey="computeCost"
                    name="Compute Cost"
                    fill={alpha(theme.palette.warning.main, 0.1)}
                    stroke={theme.palette.warning.main}
                    strokeWidth={1}
                    stackId="costs"
                    isAnimationActive={false}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DataPriceAnalysisWidget;